import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";

const MAX_RETRIES = 4;
const RETRY_INTERVAL_MS = 2500;
const FALLBACK_REDIRECT_DELAY_MS = 14_500;
const PAID_REDIRECT_DELAY_MS = 1500;

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("お支払いを確認しています...");
  const retriesRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const schedule = (callback: () => void, delayMs: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delayMs);
      timers.add(timer);
      return timer;
    };

    const clearScheduledTimers = () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };

    const redirectHome = (nextMessage: string, delayMs: number) => {
      if (cancelled) return;
      clearScheduledTimers();
      setMessage(nextMessage);
      if (delayMs <= 0) {
        navigate("/home", { replace: true });
        return;
      }
      schedule(() => {
        if (!cancelled) navigate("/home", { replace: true });
      }, delayMs);
    };

    const check = async () => {
      try {
        const accountState = await api.getMe();
        if (cancelled) return;

        if (accountState.plan === "paid") {
          redirectHome("ご契約ありがとうございます！", PAID_REDIRECT_DELAY_MS);
          return;
        }
      } catch {
        // 取得失敗は無視してリトライ
      }

      retriesRef.current += 1;
      if (retriesRef.current >= MAX_RETRIES) {
        redirectHome("まもなく反映されます。ホームに移動します...", 2000);
        return;
      }

      schedule(() => {
        if (!cancelled) void check();
      }, RETRY_INTERVAL_MS);
    };

    // APIが応答しない場合でも、決済完了画面から離れられるようにする。
    schedule(() => {
      redirectHome("まもなく反映されます。ホームに移動します...", 0);
    }, FALLBACK_REDIRECT_DELAY_MS);

    schedule(() => {
      if (!cancelled) void check();
    }, RETRY_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearScheduledTimers();
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDF8F5] px-6 text-center">
      <div className="w-full max-w-sm rounded-[1.8rem] border border-[#EADBC8] bg-white px-8 py-10 shadow-[0_16px_40px_rgba(166,124,82,0.12)]">
        <p className="text-4xl">✓</p>
        <h1 className="mt-4 text-lg font-black text-[#5D4D46]">お支払いが完了しました</h1>
        <p className="mt-3 text-sm font-medium leading-6 text-[#7B6861]">{message}</p>
      </div>
    </div>
  );
}
